import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Feature, Geometry, MultiPolygon, Polygon } from 'geojson';
import type { Layer } from '@deck.gl/core';
import { GeoJsonLayer } from '@deck.gl/layers';
import { useAreasData } from '../../data/useAreasData';
import { useMapSelection } from '../useMapSelection';
import { useMapVisibility } from '../useMapVisibility';
import { useLegendConfig } from '../useLegendConfig';
import { useMetaData } from '../../data/useMetaData';
import { useAreaAggregationData } from '../../data/useAreaAggregationData';
import { useAggregationConfig } from '../useAggregationConfig';
import { useMapColorPalette } from '../useMapColorPalette';
import { useCustomAreaCreation } from '../useCustomAreaCreation';
import { useCustomAreaEdition } from '../useCustomAreaEdition';
import type { Area } from '../../../types/domain';
import { COLORS } from '../../../constants/color';
import { alpha, toRgb, type RgbColor } from '../../../utils/color';
import {
  bordersToRanges,
  calcBins,
  calcPercentile,
} from '../../../utils/statistics';
import {
  formatCountPerArea,
  formatDistance,
  formatDuration,
} from '../../../utils/format';
import type { Legend } from '../../../types/common';
import { useDataFilter } from '../useDataFilter';

type UseAreaCityLayer = {
  areaCityLayer: Layer;
  areaCityLegends: Legend[];
};

type CustomFeature = Feature<Polygon | MultiPolygon> & {
  key_code: string;
  value?: number;
  tooltip: string;
};

const SQUARE_KILOMETER = 1000 * 1000;

const isCustomFeature = (
  feature: Feature<Geometry>
): feature is CustomFeature => {
  return (
    'key_code' in feature &&
    typeof feature.key_code === 'string' &&
    ('value' in feature
      ? typeof feature.value === 'number' ||
        typeof feature.value === 'undefined'
      : true) &&
    'tooltip' in feature &&
    typeof feature.tooltip === 'string'
  );
};

const useAreaCityLayer = (): UseAreaCityLayer => {
  const { t } = useTranslation();
  const { filteredAreaKeyCodes } = useDataFilter();
  const { areaCities } = useAreasData();
  const { aggregations } = useAreaAggregationData();
  const { selectionKind, selectedAreaKeyCodes } = useMapSelection();
  const { visibleAreaType, isAggregationVisible } = useMapVisibility();
  const { aggregationConfig } = useAggregationConfig();
  const { scaleType, scaleSize, customRange } = useLegendConfig();
  const { currentDataMeta } = useMetaData();
  const { editingCustomArea } = useCustomAreaEdition();
  const { creatingCustomArea } = useCustomAreaCreation();
  const { getRgbColors, mapColorOpacity } = useMapColorPalette();

  const isAggregating =
    aggregationConfig?.type === 'areas' && aggregations.size > 0;
  const isCount = ![
    'traveled_distance',
    'traveled_time',
    'activity_time',
  ].includes(aggregationConfig?.target ?? '');
  const viewMagnification = 1 / Number(currentDataMeta?.reduction_factor ?? 1);

  const data = useMemo((): CustomFeature[] => {
    const formatValue = (value: number): string => {
      switch (aggregationConfig?.target) {
        case 'traveled_time':
        case 'activity_time':
          return formatDuration(value);
        case 'traveled_distance':
          return formatDistance(value);
        default:
          return formatCountPerArea(value, t('unit.agent'));
      }
    };

    return Array.from(areaCities.values()).map((area: Area): CustomFeature => {
      const aggregation = isAggregating
        ? aggregations.get(`${area.key_code}`)
        : undefined;
      const value =
        typeof aggregation === 'number'
          ? aggregation * (isCount ? viewMagnification * SQUARE_KILOMETER : 1)
          : undefined;
      return {
        type: 'Feature',
        geometry: area.geometry,
        properties: {},
        key_code: area.key_code,
        value: value,
        tooltip:
          value !== undefined
            ? `${area.s_name || '-'}: ${formatValue(value)}`
            : area.s_name || '-',
      };
    });
  }, [
    areaCities,
    aggregations,
    aggregationConfig?.target,
    isAggregating,
    isCount,
    viewMagnification,
    t,
  ]);

  const legends = useMemo((): Legend[] => {
    if (!isAggregating) {
      return [];
    }

    const areaValues = Array.from(
      aggregations.values(),
      (value: number): number => {
        return value * (isCount ? viewMagnification * SQUARE_KILOMETER : 1);
      }
    );
    const ranges =
      scaleType === 'custom'
        ? customRange
        : scaleType === 'quantize'
          ? bordersToRanges(calcBins(areaValues, scaleSize, 0.01))
          : bordersToRanges(calcPercentile(areaValues, scaleSize));

    return getRgbColors(ranges.length).map(
      (color: RgbColor, index: number): Legend => {
        return {
          color: alpha(color, mapColorOpacity),
          range: ranges[index],
        };
      }
    );
  }, [
    aggregations,
    isAggregating,
    isCount,
    scaleType,
    scaleSize,
    customRange,
    getRgbColors,
    mapColorOpacity,
    viewMagnification,
  ]);

  const areaCityLayer = new GeoJsonLayer({
    id: 'area-city-layer',
    data: data,
    getFillColor: (feature: Feature<Geometry>): RgbColor => {
      // 実際には不要であるが、型エラーを避けるために新しい変数に代入してから使用する
      const customFeature = feature;
      if (!isCustomFeature(customFeature)) {
        return toRgb(COLORS.transparent);
      }

      // 集計結果がある地域については、値の大小に応じた色分けを行う
      if (isAggregationVisible && customFeature.value !== undefined) {
        return (
          legends.find((legend: Legend): boolean => {
            return (
              customFeature.value !== undefined &&
              legend.range[0] <= customFeature.value &&
              customFeature.value <= legend.range[1]
            );
          })?.color ?? toRgb(COLORS.transparent)
        );
      }
      // クリックで選択された地域を強調表示する
      if (selectedAreaKeyCodes.includes(customFeature.key_code)) {
        return toRgb(alpha('#848C12', 0.6));
      }
      // フィルターの対象になっている地域を強調表示する
      if (filteredAreaKeyCodes.includes(customFeature.key_code)) {
        return toRgb(alpha(COLORS.gray, 0.6));
      }
      // それ以外の地域はデータの対象範囲内かどうかに関わらず一律で透明にする
      return toRgb(COLORS.transparent);
    },
    getLineColor: (feature: Feature<Geometry>): RgbColor => {
      // 実際には不要であるが、型エラーを避けるために新しい変数に代入してから使用する
      const customFeature = feature;
      if (!isCustomFeature(customFeature)) {
        return toRgb(COLORS.transparent);
      }

      // クリックで選択された地域を強調表示する
      if (selectedAreaKeyCodes.includes(customFeature.key_code)) {
        return toRgb(alpha('#F2FF21', 0.3));
      }
      // 市区町村表示の場合、またはフィルターの対象になっている場合は境界線を薄く表示する
      if (
        visibleAreaType === 'city' ||
        filteredAreaKeyCodes.includes(customFeature.key_code)
      ) {
        return toRgb(alpha(COLORS.lightGray, 0.3));
      }
      return toRgb(COLORS.transparent);
    },
    getLineWidth: 10,
    lineWidthUnits: 'meters',
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 3,
    lineJointRounded: true,
    autoHighlight:
      visibleAreaType === 'city' &&
      !creatingCustomArea &&
      !editingCustomArea &&
      selectionKind !== 'link',
    highlightColor: toRgb(alpha('#848C12', 0.6)),
    pickable:
      visibleAreaType === 'city' &&
      !creatingCustomArea &&
      !editingCustomArea &&
      selectionKind !== 'link',
    visible: !!visibleAreaType,
    updateTriggers: {
      getFillColor: [
        legends,
        isAggregationVisible,
        selectedAreaKeyCodes,
        filteredAreaKeyCodes,
      ],
      getLineColor: [
        visibleAreaType,
        selectedAreaKeyCodes,
        filteredAreaKeyCodes,
      ],
    },
  });

  return {
    areaCityLayer: areaCityLayer,
    areaCityLegends: legends,
  };
};

export { useAreaCityLayer };
